import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * 自定义Hook，用于控制浏览器历史记录的回退行为
 * 
 * @param targetPath 当用户回退时希望导航到的目标路径
 * @param options 配置选项
 * @returns void
 */
export const useHistoryControl = (
    targetPath: string,
    options: {
        /**
         * 是否使用replace模式进行导航
         * true: 导航到目标路径时会替换历史记录中的当前条目
         * false: 导航到目标路径时会创建新的历史条目
         */
        useReplace?: boolean;
        /**
         * 是否启用导航按钮控制
         * true: 返回一个handleBackClick函数，用于控制导航按钮的行为
         * false: 不返回handleBackClick函数
         */
        controlNavButton?: boolean;
    } = {}
) => {
    const navigate = useNavigate();
    
    // 默认选项
    const defaultOptions = {
        useReplace: true,
        controlNavButton: true
    };
    
    // 合并选项
    const mergedOptions = { ...defaultOptions, ...options };
    
    // 处理页面上的返回按钮点击
    const handleBackClick = () => {
        // 根据选项决定是否使用replace模式
        if (mergedOptions.useReplace) {
            navigate(targetPath, { replace: true });
        } else {
            navigate(targetPath);
        }
    };
    
    // 设置浏览器历史回退行为
    useEffect(() => {
        // 如果targetPath为空字符串，则不执行历史控制逻辑
        if (!targetPath) {
            return;
        }
        
        // 获取当前路径
        const currentPath = window.location.pathname;
        
        // 创建一个新的历史会话
        const createNewHistorySession = () => {
            try {
                // 将当前URL替换为目标页面（这样当回退时会到达目标页面）
                window.history.replaceState({ backTarget: true }, '', targetPath);
                
                // 将当前页面推入历史栈
                window.history.pushState({ currentPage: true }, '', currentPath);
                
                // 监听popstate事件
                const handlePopState = (e: PopStateEvent) => {
                    const state = e.state || {};
                    
                    // 如果回退到了我们设置的带有backTarget标记的状态
                    if (state.backTarget) {
                        // 导航到目标页面
                        if (mergedOptions.useReplace) {
                            navigate(targetPath, { replace: true });
                        } else {
                            navigate(targetPath);
                        }
                    }
                };
                
                window.addEventListener('popstate', handlePopState);
                
                return () => {
                    window.removeEventListener('popstate', handlePopState);
                };
            } catch (error) {
                console.error('Error in history control:', error);
                return undefined;
            }
        };
        
        // 执行历史会话创建
        return createNewHistorySession();
        
    }, [navigate, targetPath, mergedOptions.useReplace]);
    
    // 根据controlNavButton选项决定是否返回handleBackClick函数
    return mergedOptions.controlNavButton ? { handleBackClick } : {};
};

export default useHistoryControl; 